import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import NavigationBar from 'components/Navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { reviewService, Hashtag } from 'services/reviewService';
import {
  Container,
  Header,
  BackButton,
  Content,
  MainCard,
  FacilitySection,
  FacilityImage,
  FacilityImagePlaceholder,
  FacilityInfo,
  FacilityName,
  FacilityLocation,
  UsageDate,
  RatingSection,
  ReviewSection,
  ImageSection,
  HashtagSection,
  SectionTitle,
  StarRatingContainer,
  StarButton,
  ReviewTextArea,
  ImageUploadSection,
  ImagePreviewContainer,
  ImagePreview,
  RemoveImageButton,
  UploadButton,
  HashtagList,
  HashtagButton,
  SubmitButton
} from './ReviewCreate.styles';

const ReviewEdit: React.FC = () => {
  const navigate = useNavigate();
  const { reviewId } = useParams<{ reviewId: string }>();
  const [rating, setRating] = useState<number>(0);
  const [reviewText, setReviewText] = useState<string>('');
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [hashtags, setHashtags] = useState<Hashtag[]>([]);
  const [selectedHashtagIds, setSelectedHashtagIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [review, setReview] = useState<any>(null);

  // 리뷰 정보 조회
  useEffect(() => {
    const fetchReview = async () => {
      if (!reviewId) {
        navigate('/reviews');
        return;
      }

      try {
        const data = await reviewService.getReviewById(parseInt(reviewId));
        // API 응답의 startRating을 starRating으로 매핑
        const mappedReview = {
          ...data,
          starRating: (data as any).startRating || data.starRating
        };

        setReview(mappedReview);
        setRating(mappedReview.starRating);
        setReviewText(mappedReview.content);
        setExistingImageUrls(mappedReview.images || []);
        setImagePreviews(mappedReview.images || []);
      } catch (error) {
        console.error('리뷰 조회 실패:', error);
        alert('리뷰 정보를 불러오는데 실패했습니다.');
        navigate('/reviews');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReview();
  }, [reviewId, navigate]);

  useEffect(() => {
    const fetchHashtags = async () => {
      try {
        const data = await reviewService.getHashtags();
        setHashtags(data);
      } catch (error) {
        console.error('Failed to fetch hashtags:', error);
      }
    };

    fetchHashtags();
  }, []);

  const handleStarClick = (selectedRating: number) => {
    setRating(selectedRating);
  };

  const handleHashtagToggle = (hashtagId: number) => {
    setSelectedHashtagIds(prev =>
      prev.includes(hashtagId)
        ? prev.filter(id => id !== hashtagId)
        : [...prev, hashtagId]
    );
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newImages = Array.from(files);
    const totalImages = images.length + existingImageUrls.length + newImages.length;

    if (totalImages > 3) {
      alert('최대 3장까지 업로드 가능합니다.');
      return;
    }

    setImages([...images, ...newImages]);

    newImages.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index: number) => {
    // 기존 이미지인지 새로 추가한 이미지인지 확인
    if (index < existingImageUrls.length) {
      // 기존 이미지 제거
      setExistingImageUrls(existingImageUrls.filter((_, i) => i !== index));
      setImagePreviews(imagePreviews.filter((_, i) => i !== index));
    } else {
      // 새로 추가한 이미지 제거
      const newImageIndex = index - existingImageUrls.length;
      setImages(images.filter((_, i) => i !== newImageIndex));
      setImagePreviews(imagePreviews.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      alert('별점을 선택해주세요.');
      return;
    }

    if (!reviewText.trim()) {
      alert('리뷰를 작성해주세요.');
      return;
    }

    if (!reviewId) {
      alert('리뷰 정보가 없습니다.');
      return;
    }

    setIsSubmitting(true);

    try {
      // 새로 추가한 이미지가 있으면 업로드
      let newImages: string[] = [];
      if (images.length > 0) {
        newImages = await reviewService.uploadReviewImages(images);
      }

      // 기존 이미지 URL과 새로 업로드한 이미지 URL 합치기
      const allImages = [...existingImageUrls, ...newImages];

      // 리뷰 수정 API 호출
      await reviewService.updateReview(parseInt(reviewId), {
        content: reviewText,
        starRating: rating,
        images: allImages,
        hashTagIds: selectedHashtagIds
      });

      alert('리뷰가 수정되었습니다.');
      navigate('/reviews');
    } catch (error: any) {
      console.error('리뷰 수정 실패:', error);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert('리뷰 수정에 실패했습니다. 다시 시도해주세요.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  if (isLoading) {
    return (
      <>
        <Container>
          <Header>
            <BackButton onClick={() => navigate(-1)}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </BackButton>
            리뷰 수정
          </Header>
          <Content>
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              로딩 중...
            </div>
          </Content>
        </Container>
        <NavigationBar />
      </>
    );
  }

  if (!review) {
    return null;
  }

  const locationParts = [
    review.facility.building?.name
  ].filter(Boolean);

  return (
    <>
      <Container>
        <Header>
          <BackButton onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </BackButton>
          리뷰 수정
        </Header>
        <Content>
          <MainCard>
            <FacilitySection>
              {review.facility.imageUrl ? (
                <FacilityImage src={review.facility.imageUrl} alt={review.facility.name} />
              ) : (
                <FacilityImagePlaceholder>🏢</FacilityImagePlaceholder>
              )}
              <FacilityInfo>
                <FacilityName>{review.facility.name}</FacilityName>
                <FacilityLocation>{locationParts.join(' ')}</FacilityLocation>
              </FacilityInfo>
              <UsageDate>작성일: {formatDate(review.createdAt)}</UsageDate>
            </FacilitySection>

            <RatingSection>
              <SectionTitle>별점을 선택해주세요.</SectionTitle>
              <StarRatingContainer>
                {[1, 2, 3, 4, 5].map((star) => (
                  <StarButton
                    key={star}
                    filled={star <= rating}
                    onClick={() => handleStarClick(star)}
                  >
                    ★
                  </StarButton>
                ))}
              </StarRatingContainer>
            </RatingSection>

            <ReviewSection>
              <SectionTitle>자세한 리뷰를 남겨주세요.</SectionTitle>
              <ReviewTextArea
                placeholder="리뷰를 입력하세요"
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
              />
            </ReviewSection>

            <ImageSection>
              <ImageUploadSection>
                {imagePreviews.length > 0 && (
                  <ImagePreviewContainer>
                    {imagePreviews.map((preview, index) => (
                      <ImagePreview key={index}>
                        <img src={preview} alt={`Preview ${index + 1}`} />
                        <RemoveImageButton onClick={() => handleRemoveImage(index)}>
                          ×
                        </RemoveImageButton>
                      </ImagePreview>
                    ))}
                  </ImagePreviewContainer>
                )}
                {(images.length + existingImageUrls.length) < 3 && (
                  <UploadButton>
                    📷 사진 첨부하기
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                    />
                  </UploadButton>
                )}
              </ImageUploadSection>
            </ImageSection>

            <HashtagSection>
              <SectionTitle>태그를 선택해주세요. (다중 선택 가능)</SectionTitle>
              <HashtagList>
                {hashtags.map((hashtag) => (
                  <HashtagButton
                    key={hashtag.id}
                    selected={selectedHashtagIds.includes(hashtag.id)}
                    onClick={() => handleHashtagToggle(hashtag.id)}
                    type="button"
                  >
                    #{hashtag.name}
                  </HashtagButton>
                ))}
              </HashtagList>
            </HashtagSection>
          </MainCard>

          <SubmitButton onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? '수정 중...' : '수정하기'}
          </SubmitButton>
        </Content>
      </Container>
      <NavigationBar />
    </>
  );
};

export default ReviewEdit;
